package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.StoreAppDto
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.shared.StoreImportResultDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.checkStoreConnection
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Text

/**
 * One store's card: not connected, connected with no app, or bound to an app.
 *
 * The card always says which of the three it is in a badge beside the store's name, so a reader
 * scanning two cards knows where each store stands before reading either.
 */
@Composable
internal fun StorePlatformCard(
    platform: StorePlatform,
    app: StoreAppDto?,
    connections: List<StoreConnectionDto>?,
    data: StoresPageData,
    runner: StoreActionRunner,
    onBindingChanged: () -> Unit,
    onListingChanged: () -> Unit,
) {
    val usable = connections?.let { usableStoreConnections(it, platform) }.orEmpty()
    LCard(Modifier.fillMaxWidth().styleModifier { property("min-width", "0"); property("overflow-wrap", "anywhere") }) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Row(
                Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LSectionTitle(platform.displayName)
                when {
                    app != null && connections?.firstOrNull { it.id == app.connectionId }?.status == dev.lingvo.shared.StoreConnectionStatus.REJECTED ->
                        LBadge(t("app.projects.slug.stores_connect.state_broken", "Connection needs attention"), variant = LBadgeVariant.DANGER)
                    app != null -> LBadge(t("app.projects.slug.stores_connect.state_bound", "App bound"), variant = LBadgeVariant.SUCCESS)
                    usable.isNotEmpty() -> LBadge(t("app.projects.slug.stores_connect.state_connected", "Connected, no app bound"), variant = LBadgeVariant.INFO)
                    connections == null -> LBadge(t("app.projects.slug.stores_connect.state_no_app", "No app bound"))
                    else -> LBadge(t("app.projects.slug.stores_connect.state_not_connected", "Not connected"))
                }
            }
            when {
                app != null -> StoreBoundApp(
                    platform = platform,
                    app = app,
                    connection = connections?.firstOrNull { it.id == app.connectionId },
                    data = data,
                    runner = runner,
                    onBindingChanged = onBindingChanged,
                    onListingChanged = onListingChanged,
                )
                !runner.canManage -> {
                    LMuted(
                        t(
                            "app.projects.slug.stores_connect.read_only_not_bound",
                            "No app is bound for this store yet. An owner or admin connects the store account and binds the app.",
                        ),
                    )
                    LButton(
                        t("app.projects.slug.stores_connect.connect", "Connect"),
                        onClick = {},
                        variant = LButtonVariant.SECONDARY,
                        enabled = false,
                    )
                }
                connections == null -> LMuted(
                    t(
                        "app.projects.slug.stores_connect.connections_missing",
                        "The workspace's connections didn't load, so this store can't be connected from here right now. Reload to try again.",
                    ),
                )
                else -> StoreConnectAndBind(platform, usable, data, runner, onBindingChanged)
            }
        }
    }
}

/** A bound app: what it is, which key reaches it, and the import that brings its listing in. */
@Composable
private fun StoreBoundApp(
    platform: StorePlatform,
    app: StoreAppDto,
    connection: StoreConnectionDto?,
    data: StoresPageData,
    runner: StoreActionRunner,
    onBindingChanged: () -> Unit,
    onListingChanged: () -> Unit,
) {
    val slug = data.project.slug
    var busy by remember(app.id) { mutableStateOf(false) }
    var result by remember(app.id) { mutableStateOf<StoreImportResultDto?>(null) }
    var confirmOverwrite by remember(app.id) { mutableStateOf(false) }
    var confirmUnbind by remember(app.id) { mutableStateOf(false) }

    fun import(overwrite: Boolean) {
        runner.run(
            setBusy = { busy = it },
            failure = tr("app.projects.slug.stores_connect.import_failed", "Couldn't import the listing. Retry?"),
            transport = { ApiClient.importStoreListing(slug, platform, overwrite) },
        ) { imported ->
            result = imported
            confirmOverwrite = false
            runner.notify(
                tr("app.projects.slug.stores_connect.imported_toast", "Listing imported"),
                LToastVariant.SUCCESS,
                4_000,
            )
            onListingChanged()
        }
    }

    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
        // The id has its own line below; repeating it as a heading only when there is no name to show.
        app.displayName?.takeIf(String::isNotBlank)?.let { Text(it) }
        LMuted(
            trf(
                "app.projects.slug.stores_connect.app_ref_line",
                "{label}: {ref}",
                "label" to storeAppRefLabel(platform),
                "ref" to app.storeAppRef,
            ),
        )
        if (connection != null) StoreConnectionSummary(connection)
        LMuted(
            app.lastImportedAt?.let {
                trf(
                    "app.projects.slug.stores_connect.last_imported",
                    "Last imported {time}",
                    "time" to formatUtc(it, LTimeStyle.MINUTE),
                )
            } ?: tr("app.projects.slug.stores_connect.never_imported", "Not imported yet."),
        )
    }
    Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
        LButton(
            t("app.projects.slug.stores_connect.import_current_listing", "Import current listing"),
            onClick = { import(overwrite = false) },
            variant = if (app.lastImportedAt == null) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
            enabled = runner.canManage && !busy,
            loading = busy,
        )
        LButton(
            t("app.projects.slug.stores_connect.unbind", "Unbind app"),
            onClick = { confirmUnbind = true },
            variant = LButtonVariant.GHOST,
            enabled = runner.canManage && !busy,
        )
    }
    if (connection?.status == dev.lingvo.shared.StoreConnectionStatus.REJECTED && runner.canManage) {
        LAlert(t("app.projects.slug.stores_connect.access_lost", "Store access was lost"),
            t("app.projects.slug.stores_connect.reconnect_help", "Restore the key's app access or re-invite the service account in Play Console. Then verify access again."),
            variant = LAlertVariant.WARNING, live = true)
    }
    if (runner.canManage && connection != null) {
        LButton(t("app.projects.slug.stores_connect.reverify", "Verify access again"), onClick = {
            runner.run(setBusy = { busy = it }, failure = tr("app.projects.slug.stores_connect.reverify_failed", "Couldn't verify access. Try again."),
                transport = { ApiClient.checkStoreConnection(data.orgSlug, connection.id, app.storeAppRef) },
            ) { onBindingChanged() }
        }, variant = LButtonVariant.SECONDARY, enabled = !busy, loading = busy)
    }
    result?.let { StoreImportResult(it, slug, runner.canManage && !busy) { confirmOverwrite = true } }

    LConfirmDialog(
        open = confirmOverwrite,
        title = t("app.projects.slug.stores_connect.replace_local_title", "Replace local edits?"),
        confirmLabel = t("app.projects.slug.stores_connect.replace_local_confirm", "Replace with the store's text"),
        onConfirm = { import(overwrite = true) },
        onCancel = { confirmOverwrite = false },
        tone = LConfirmTone.DANGER,
        consequenceTitle = t("app.projects.slug.stores_connect.replace_local_consequence_title", "Your edits are replaced"),
        consequence = t(
            "app.projects.slug.stores_connect.replace_local_consequence",
            "Every listing field that differs from the store is overwritten with what the store holds now. " +
                "The text you replace stays in each key's history.",
        ),
        busy = busy,
    )
    LConfirmDialog(
        open = confirmUnbind,
        title = tf(
            "app.projects.slug.stores_connect.unbind_title",
            "Unbind this app from {store}?",
            "store" to platform.displayName,
        ),
        confirmLabel = t("app.projects.slug.stores_connect.unbind", "Unbind app"),
        onConfirm = {
            runner.run(
                setBusy = { busy = it },
                failure = tr("app.projects.slug.stores_connect.unbind_failed", "Couldn't unbind the app. Retry?"),
                transport = { ApiClient.unbindStoreApp(slug, platform) },
            ) {
                confirmUnbind = false
                runner.notify(tr("app.projects.slug.stores_connect.unbound_toast", "App unbound"), LToastVariant.SUCCESS, 4_000)
                onBindingChanged()
            }
        },
        onCancel = { confirmUnbind = false },
        tone = LConfirmTone.DANGER,
        consequenceTitle = t("app.projects.slug.stores_connect.unbind_consequence_title", "The store is not touched"),
        consequence = t(
            "app.projects.slug.stores_connect.unbind_consequence",
            "Nothing changes in the store. The listing text stays in the project, and you can bind the app again later.",
        ),
        busy = busy,
    )
}

/** Which key reaches the store, told by its non-secret parts only. */
@Composable
internal fun StoreConnectionSummary(connection: StoreConnectionDto) {
    Row(
        Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(connection.label)
        LBadge(storeCredentialHintText(connection))
        LBadge(
            storeConnectionStatusLabel(connection.status),
            variant = storeConnectionStatusVariant(connection.status),
        )
    }
    connection.identity.forEach { (key, value) ->
        LMuted(
            trf(
                "app.projects.slug.stores_connect.identity_line",
                "{label}: {value}",
                "label" to storeIdentityLabel(key),
                "value" to storeIdentityValue(key, value),
            ),
        )
    }
    connection.lastErrorCode?.let { code ->
        LMuted(
            trf(
                "app.projects.slug.stores_connect.last_error",
                "Last error from the store: {code}",
                "code" to code,
            ),
        )
    }
}

/** What an import brought in, what it left out and why, and how to bring the rest in. */
@Composable
private fun StoreImportResult(
    result: StoreImportResultDto,
    slug: String,
    canReplace: Boolean,
    onReplace: () -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        if (result.importedLocales.isEmpty()) {
            LAlert(
                t("app.projects.slug.stores_connect.imported_nothing", "Nothing new was imported"),
                t(
                    "app.projects.slug.stores_connect.imported_nothing_detail",
                    "The project already holds the store's text, or none of the store's languages is in the project.",
                ),
                variant = LAlertVariant.INFO,
                live = true,
            )
        } else {
            LAlert(
                tPlural(
                    "app.projects.slug.stores_connect.imported_summary",
                    result.importedLocales.size,
                    "Imported {fields} fields in {count} language",
                    "Imported {fields} fields in {count} languages",
                    "fields" to result.fieldCount,
                ),
                result.importedLocales.joinToString(", "),
                variant = LAlertVariant.SUCCESS,
                live = true,
            )
        }
        if (result.skippedLocales.isNotEmpty()) {
            LAlert(
                tf(
                    "app.projects.slug.stores_connect.skipped_summary",
                    "Not imported: {locales}",
                    "locales" to result.skippedLocales.joinToString(", "),
                ),
                t(
                    "app.projects.slug.stores_connect.skipped_detail",
                    "The store has these languages and the project doesn't. Add the language to the project, then import again.",
                ),
                variant = LAlertVariant.WARNING,
                action = {
                    LLinkButton(
                        t("app.projects.slug.stores_connect.add_language", "Add a language"),
                        "/app/projects/$slug/settings?tab=languages",
                        variant = LButtonVariant.SECONDARY,
                        size = LButtonSize.SM,
                    )
                },
            )
        }
        result.warnings.forEach { warning ->
            LAlert(
                t("app.projects.slug.stores_connect.import_note", "Note from the import"),
                warning,
                variant = LAlertVariant.INFO,
            )
        }
        Row(Modifier.gap(Space.SM)) {
            LButton(
                t("app.projects.slug.stores_connect.replace_local_button", "Import again, replacing local edits"),
                onClick = onReplace,
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = canReplace,
            )
        }
    }
}
